import styles from "./styles.module.scss";
import UserAvatar from "../UserAvatar";
import MoreIcon from "app/images/more.svg";
import OnlinePayment from "app/images/online-payment.svg";
import OfflinePayment from "app/images/offline-payment.svg";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import SquaredColorOption from "../SquaredColorOption";
import {
  constructCloneProps,
  getColorCode,
} from "../../../routes/BuildProgram/Pricing/utils";
import ProgramActionMenu from "../ProgramActionMenu";
import { useHistory } from "react-router-dom";
import {
  setCloneData,
  setDefaultImages,
  setId,
} from "../../../actions/scheduling/program";
import useCurrentFacility from "app/utils/hooks/scheduling/useCurrentFacility";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import { DateTime } from "luxon";
import {
  formatMonthDay,
  formatMonthDayYear,
  formatTime,
} from "app/utils/luxonFormats";
import useCalendarPreferences from "app/utils/hooks/useCalendarPreferences";
import { useTranslation } from "react-i18next";

const ProgramItem = (props) => {
  const attributes = props;
  const { t } = useTranslation();
  const { preferences } = useCalendarPreferences();

  const startDateTime = DateTime.fromISO(attributes.start, {
    zone: props?.timeZone,
  });
  const endDateTime = DateTime.fromISO(attributes.endTime, {
    zone: props?.timeZone,
  });
  const startTime = formatMonthDayYear(startDateTime, preferences.dateFormat);
  const endTime = formatMonthDayYear(endDateTime, preferences.dateFormat);
  const subtitleDateTime = DateTime.fromISO(attributes.start, { setZone: true });
  const formattedSubtitle = attributes.start
    ? `${subtitleDateTime.toLocaleString({ weekday: "long" })}, ${formatTime(subtitleDateTime, preferences.timeFormat)}`
    : attributes.subtitle;
  const dropdownRef = useRef(null);
  const history = useHistory();
  const [displayActions, setDisplayActions] = useState(false);
  const [actionsPosition, setActionsPosition] = useState({
    top: "110%",
    bottom: "",
  });
  const dispatch = useDispatch();
  const currentFacility = useCurrentFacility();
  const schedulingInterval = currentFacility?.data?.featureFlags?.schedulingInterval || 15;
  const handleClickOutside = useCallback(
    (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        props.setDisplayActions(false);
      }
    },
    [props]
  );

  useEffect(() => {
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [handleClickOutside]);

  const handleActionsClick = (event) => {
    if (event.clientY + 178 >= window.innerHeight) {
      setActionsPosition({ top: "110%", bottom: "20px" });
    }
    event.stopPropagation();
    props.onClick();
    setDisplayActions((prev) => {
      if (props.selected && prev) {
        return false;
      }
      return true;
    });
  };

  const getTimeFromISO = (isoDate, timeZone) => {
    return DateTime.fromISO(isoDate, { zone: timeZone }).toFormat('HH:mm');
  };

  const parseTimeRangeEnd = (timeRange, timeZone) => {
    const [, endPart] = timeRange.split('...');
    const utcDateTime = DateTime.fromFormat(
      endPart.trim(),
      "yyyy-MM-dd HH:mm:ss 'UTC'",
      { zone: 'UTC' }
    );
    return utcDateTime.setZone(timeZone).toISO({ includeOffset: false });
  };

  const handleClone = (edit) => {
    if (edit && attributes.recurrenceGroupId !== null && attributes.attendance === 'clinic') {
       // For recurring clinics edit, fetch program data and set page to 4
       schedulingFetchGet(`/programs/${props.id}`).then((result) => {
         dispatch(setCloneData({
           page: 4,
           edit: true,
           id: props.id,
           recurrenceGroup: result.recurrenceGroup,
           coaches: result.coaches,
           // CN-2843: capacity is a column on the clinic, not on the group, so
           // the series form has nothing to show it from unless we carry it.
           capacity: result.capacity,
           // Same for privacy: without it a series created private could
           // never be made public.
           privacy: result.privacy,
         }));

         history.push(`/programs/build`);
       });

       return;
    }

    schedulingFetchGet("/default_images").then((response) => {
      dispatch(setDefaultImages(response));
    });
    schedulingFetchGet(`/programs/${props.id}`).then((result) => {
      const responseAttributes = result;

      const events = responseAttributes.events;
      const timeZone = responseAttributes.timeZone;

      const startTime = getTimeFromISO(responseAttributes.start, timeZone);
      const endTime = getTimeFromISO(responseAttributes.end, timeZone);

      const registrationParsedStartTime = getTimeFromISO(
        responseAttributes.registrationStartTime,
        timeZone);
      const registrationParsedEndTime = getTimeFromISO(
        responseAttributes.registrationEndTime,
        timeZone);

      const untilDate = parseTimeRangeEnd(
        events[events.length - 1].timeRange,
        timeZone
      );

      const privacy = responseAttributes.privacy;
      const attributes = {
        ...result,
        startTime,
        endTime,
        untilDate:
          responseAttributes.attendance !== "clinic" ? untilDate : null,
        registrationParsedStartTime,
        registrationParsedEndTime,
        privacy,
      };

      const cloneState = constructCloneProps(
        attributes,
        edit,
        false,
        schedulingInterval,
        preferences.timeFormat,
      );
      dispatch(setCloneData(cloneState));

      history.push(`/programs/build`);
    });
  };

  const handleView = () => {
    let url = `/programs/${props.id}/view`;
    dispatch(setId(props.id));
    if (props.isPublic) {
      url = `/public/programs/${props.id}/view`;
    }
    history.push(url);
  };

  const checkYear = () => {
    if (startDateTime.year === endDateTime.year) {
      const startNoYear = formatMonthDay(startDateTime, preferences.dateFormat);
      return `${startNoYear} - ${endTime}`;
    }
    return `${startTime} - ${endTime}`;
  };

  return (
    <div
      className={`${styles.program_item} ${
        props.isStudent && styles.clickable
      }`}
      onClick={props.isStudent ? handleView : () => null}
    >
      <div onClick={handleView}>
        <UserAvatar
          src={attributes?.externalThumbnailUrl}
          height={48}
          width={48}
          extraContainerStyles={styles.image_container}
        />
      </div>
      <div className={styles.title_with_actions}>
        <div className={styles.title}>
          <div className={styles.title_container}>
            <div className={styles.title_text} onClick={handleView}>
              <span>{attributes.title}</span>
            </div>
            <div className={styles.subtitle_text}>
              <span>{formattedSubtitle}</span>
            </div>
          </div>
        </div>
        {!props.isStudent && (
          <div className={styles.action_container} onClick={handleActionsClick}>
            <img src={MoreIcon} alt="arrow_down" data-testid="arrow-up" />

            {displayActions && props.selected ? (
              <ProgramActionMenu
                isActive={props.isActive}
                dropdownRef={dropdownRef}
                handleClone={handleClone}
                handleView={handleView}
                position={actionsPosition}
                {...props}
              />
            ) : null}
          </div>
        )}
      </div>
      <p className={styles.dates}>
        {startTime === endTime ? startTime : checkYear()}
      </p>
      <div className={styles.collect_icons}>
        {attributes.free ? (
          <span className={styles.free_label}>{t("Free")}</span>
        ) : (
          <>
            {(attributes.collectType === "online" ||
              attributes.collectType === "both") && (
              <div className={styles.iconWithTooltip}>
                <img
                  src={OnlinePayment}
                  alt="Online Payment"
                  className={styles.collectIcon}
                />
                <span className={styles.tooltipText}>Collect Online</span>
              </div>
            )}
            {(attributes.collectType === "offline" ||
              attributes.collectType === "both") && (
              <div className={styles.iconWithTooltip}>
                <img
                  src={OfflinePayment}
                  alt="Offline Payment"
                  className={styles.collectIcon}
                />
                <span className={styles.tooltipText}>Collect Offline</span>
              </div>
            )}
          </>
        )}
      </div>
      <div
        className={
          props.isStudent
            ? styles.spots_container_student
            : styles.spots_container
        }
      >
        <p className={styles.spots}>{attributes.spotsBooked}</p>
        <p className={styles.spots_18}>/{attributes.capacity}</p>
      </div>
      <div className={props.isStudent ? styles.status_student : styles.status}>
        {props.isActive && (
          <SquaredColorOption
            color={getColorCode(attributes.recurrenceGroupStatusCode || attributes.registrationStatusCode)}
            text={attributes.recurrenceGroupStatus || attributes.registrationStatus}
          />
        )}
      </div>
    </div>
  );
};

export default ProgramItem;
